import { create } from "zustand";
import { v4 as uuidv4 } from "uuid";
import {
  TrussPlane,
  TrussOrientation,
  Truss,
  TrussState,
  TrussType,
} from "../types/truss";
import { validateNodeLoadcaseReferences } from "../utils/structuralValidation";
import { useModelStore } from "@/store/useModelStore";

interface TrussStore extends TrussState {
  getAllNodeTrusses: any;
  getGroupedTruss: () => any;
  // Tool management
  setActiveTool: (active: boolean) => void;
  setSelectedTrussType: (type: TrussType) => void;

  // Preset tracking
  selectedPresetName: string | null;
  setSelectedPresetName: (name: string | null) => void;

  // Truss operations
  addTruss: (
    nodeIds: string[],
    type: TrussType,
    plane: TrussPlane,
    orientation?: TrussOrientation,
    color?: string,
    rotationValue?: number,
    label?: string
  ) => void;
  removeTruss: (trussId: string) => void;
  removeTrussesByLabel: (label: string) => void;
  updateTruss: (trussId: string, updates: Partial<Truss>) => void;
  getTrussesForNode: (nodeIds: string[], type?: TrussType) => Truss[];

  // Selection
  setSelectedTruss: (trussId: string | null) => void;

  // Visibility controls
  toggleTrussVisibility: (trussId: string) => void;
  toggleAllTrussVisibility: (visible?: boolean) => void;
  areAllTrussesVisible: () => boolean;
  showTrussForNode: (nodeIds: string[]) => void;
  hideTrussForNode: (nodeIds: string[]) => void;

  // Naming sequence management
  getNextTrussName: (nodeIds: string[], type: TrussType) => string;
  renumberTrusses: (nodeIds: string[], type: TrussType) => void;
  applyLoadOnNode: (
    nodeIds: string[],
    rotationValues: Record<TrussPlane, number>,
    perpendicularValues: Record<TrussPlane, number>
  ) => void;

  clearAllTrusses: () => void;
}

// --- Store Implementation ---
export const useTrussStore = create<TrussStore>((set, get) => ({
  activeTool: false,
  selectedTrussId: null,
  nodeTrusses: [],
  regularTrussCounter: 1,
  perpendicularTrussCounter: 1,
  selectedTrussType: "regular",
  selectedPresetName: null,

  setSelectedPresetName: (name) => set({ selectedPresetName: name }),

  setActiveTool: (active) => set({ activeTool: active }),
  setSelectedTrussType: (type) => set({ selectedTrussType: type }),

  getNextTrussName: (nodeIds, type) => {
    const existingTrusses = get().nodeTrusses.filter(
      (t) => t.type === type && t.nodeIds.some((id) => nodeIds.includes(id))
    );

    const prefix = type === "regular" ? "m" : "p";
    return `${prefix}${existingTrusses.length + 1}`;
  },

  applyLoadOnNode: (
    nodeIds: string[],
    rotationValues: Record<TrussPlane, number>,
    perpendicularValues: Record<TrussPlane, number>
  ) => {
    //  Ensure nodeIds is always array
    const validNodeIds = Array.isArray(nodeIds)
      ? nodeIds.filter((id) => id)
      : [];

    // Validate node references exist (only if nodeIds provided)
    if (validNodeIds.length > 0) {
      const modelStore = useModelStore.getState();
      const existingNodeIds = modelStore.nodes.map((n) => n.id);

      const validationErrors = validateNodeLoadcaseReferences(
        [{ name: "Node Load Application", targetIds: validNodeIds }],
        existingNodeIds
      );

      if (validationErrors.length > 0) {
        console.warn(
          "⚠️ Node loadcase validation warning:",
          validationErrors[0].message
        );
        // Filter out invalid node IDs
        nodeIds = validNodeIds.filter((nid) => existingNodeIds.includes(nid));
        validNodeIds.length = 0;
        validNodeIds.push(...nodeIds);
      }

      // Don't apply if no valid nodes remain
      if (validNodeIds.length === 0) {
        console.warn("⚠️ No valid nodes for load application, skipping");
        return;
      }
    }

    const { getTrussesForNode, removeTruss, addTruss, selectedPresetName } =
      get();

    // Only remove existing trusses that belong to the SAME preset (load)
    // This allows different loads from different loadcases on the same node
    const regularTrusses = getTrussesForNode(validNodeIds, "regular");
    const perpendicularTrusses = getTrussesForNode(
      validNodeIds,
      "perpendicular"
    );

    (["X", "Y", "Z"] as TrussPlane[]).forEach((axis) => {
      const rotVal = rotationValues?.[axis];
      const perpVal = perpendicularValues?.[axis];

      // --- Only remove existing trusses of same plane AND same preset name
      const existingRegular = regularTrusses.find(
        (t) =>
          t.plane === axis &&
          (selectedPresetName ? t.presetName === selectedPresetName : true)
      );
      const existingPerp = perpendicularTrusses.find(
        (t) =>
          t.plane === axis &&
          (selectedPresetName ? t.presetName === selectedPresetName : true)
      );
      if (existingRegular) removeTruss(existingRegular.id);
      if (existingPerp) removeTruss(existingPerp.id);

      // --- Apply new rotation truss (regular) - Allow empty nodeIds for imported presets
      if (rotVal && rotVal !== 0) {
        addTruss(validNodeIds, "regular", axis, undefined, undefined, rotVal);
      }

      // --- Apply perpendicular truss - Allow empty nodeIds for imported presets
      if (perpVal && perpVal !== 0) {
        addTruss(
          validNodeIds,
          "perpendicular",
          axis,
          undefined,
          undefined,
          perpVal
        );
      }
    });
  },

  renumberTrusses: (nodeIds, type) => {
    set((state) => {
      const prefix = type === "regular" ? "m" : "p";

      let counter = 1;

      const updated = state.nodeTrusses.map((t) => {
        if (t.type === type && t.nodeIds.some((id) => nodeIds.includes(id))) {
          return {
            ...t,
            label: `${prefix}${counter++}`,
          };
        }
        return t;
      });

      return { nodeTrusses: updated };
    });
  },

  addTruss: (
    nodeIds,
    type,
    plane,
    orientation,
    color,
    rotationValue,
    label
  ) => {
    const validNodeIds = Array.isArray(nodeIds)
      ? nodeIds.filter((id) => id)
      : [];

    // Validate node references exist (only if nodes provided)
    if (validNodeIds.length > 0) {
      const modelStore = useModelStore.getState();
      const existingNodeIds = modelStore.nodes.map((n) => n.id);

      const validationErrors = validateNodeLoadcaseReferences(
        [
          {
            name: label || get().getNextTrussName(validNodeIds, type),
            targetIds: validNodeIds,
          },
        ],
        existingNodeIds
      );

      if (validationErrors.length > 0) {
        console.warn(
          "⚠️ Node loadcase validation warning:",
          validationErrors[0].message
        );
        // Filter out invalid node IDs
        nodeIds = nodeIds.filter((nid) => existingNodeIds.includes(nid));
      }

      // Don't create truss if no valid nodes remain AND nodes were originally provided
      if (validNodeIds.length === 0 && nodeIds.length > 0) {
        console.warn("⚠️ No valid nodes for truss, skipping creation");
        return;
      }

      // Validate no duplicate loadcase assignments (only for assigned nodes)
      const state = get();
      const nodeTrusses = state.nodeTrusses;

      // Check if any of the target nodes already have a truss of the same type and plane
      const conflictingNodes: string[] = [];
      validNodeIds.forEach((nodeId) => {
        const existing = nodeTrusses.find(
          (t) =>
            t.nodeIds.includes(nodeId) && t.type === type && t.plane === plane
        );
        if (existing) {
          conflictingNodes.push(
            `Node ${nodeId} (already has "${existing.label}" for ${type} on ${plane} axis)`
          );
        }
      });

      if (conflictingNodes.length > 0) {
        throw new Error(
          `Cannot create truss: ${conflictingNodes.join(", ")}. Each node can only have one ${type} load per axis.`
        );
      }
    }

    set((state) => ({
      nodeTrusses: [
        ...state.nodeTrusses,
        {
          id: uuidv4(),
          nodeIds: [...validNodeIds],
          type,
          plane,
          orientation,
          color,
          visible: true,
          label: label || get().getNextTrussName(validNodeIds, type),
          rotationValue,
          presetName: get().selectedPresetName ?? null,
        },
      ],
    }));
  },

  removeTruss: (trussId) => {
    set((state) => ({
      nodeTrusses: state.nodeTrusses.filter((t) => t.id !== trussId),
      selectedTrussId:
        state.selectedTrussId === trussId ? null : state.selectedTrussId,
    }));
  },

  removeTrussesByLabel: (label: string) => {
    set((state) => ({
      nodeTrusses: state.nodeTrusses.filter(
        (t) => t.label !== label && t.presetName !== label
      ),
      selectedTrussId:
        state.selectedTrussId &&
        state.nodeTrusses.find((t) => t.id === state.selectedTrussId)?.label ===
          label
          ? null
          : state.selectedTrussId,
    }));
  },

  updateTruss: (trussId, updates) => {
    set((state) => ({
      nodeTrusses: state.nodeTrusses.map((t) =>
        t.id === trussId ? { ...t, ...updates } : t
      ),
    }));
  },

  getTrussesForNode: (nodeIds, type) => {
    const ids = Array.isArray(nodeIds)
      ? nodeIds.map(String).filter(Boolean)
      : [];

    return get().nodeTrusses.filter((t) => {
      if (!Array.isArray(t.nodeIds) || t.nodeIds.length === 0) return false;

      const applies = t.nodeIds.some((id) => ids.includes(id));
      if (!applies) return false;

      return type ? t.type === type : true;
    });
  },

  setSelectedTruss: (trussId) => set({ selectedTrussId: trussId }),

  toggleTrussVisibility: (trussId) => {
    set((state) => ({
      nodeTrusses: state.nodeTrusses.map((t) =>
        t.id === trussId ? { ...t, visible: !t.visible } : t
      ),
    }));
  },

  showTrussForNode: (nodeIds) => {
    set((state) => ({
      nodeTrusses: state.nodeTrusses.map((t) =>
        t.nodeIds.some((id) => nodeIds.includes(id))
          ? { ...t, visible: true }
          : t
      ),
    }));
  },

  hideTrussForNode: (nodeIds) => {
    set((state) => ({
      nodeTrusses: state.nodeTrusses.map((t) =>
        t.nodeIds.some((id) => nodeIds.includes(id))
          ? { ...t, visible: false }
          : t
      ),
    }));
  },

  toggleAllTrussVisibility: (visible) => {
    set((state) => {
      const nextVisible =
        visible !== undefined
          ? visible
          : !state.nodeTrusses.every((t) => t.visible);

      return {
        nodeTrusses: state.nodeTrusses.map((t) => ({
          ...t,
          visible: nextVisible,
        })),
      };
    });
  },

  areAllTrussesVisible: () => {
    const trusses = get().nodeTrusses;
    return trusses.length > 0 && trusses.every((t) => t.visible);
  },

  getAllNodeTrusses: () => get().nodeTrusses,

  getGroupedTruss: () => {
    const grouped = new Map<string, any>();

    get().nodeTrusses.forEach((t) => {
      const key = t.presetName ?? t.label;

      if (!grouped.has(key)) {
        grouped.set(key, {
          id: key,
          name: key,
          type: t.type,
          nodeIds: [],
          rotationValues: { X: 0, Y: 0, Z: 0 },
          perpendicularValues: { X: 0, Y: 0, Z: 0 },
          visible: t.visible,
        });
      }

      const g = grouped.get(key);

      t.nodeIds.forEach((id) => {
        if (!g.nodeIds.includes(id)) g.nodeIds.push(id);
      });

      if (t.type === "regular") {
        g.rotationValues[t.plane] = t.rotationValue ?? 0;
      } else {
        g.perpendicularValues[t.plane] = t.rotationValue ?? 0;
      }
    });

    return Array.from(grouped.values());
  },

  clearAllTrusses: () => {
    set({
      nodeTrusses: [],
      selectedTrussId: null,
      activeTool: false,
      regularTrussCounter: 1,
      perpendicularTrussCounter: 1,
      selectedPresetName: null,
    });
  },
}));
